import { CurrencyCode } from '@commutifi/constants/Locale';

/**
 * Compact display symbols for the currencies we surface in pickers and
 * formatted-cost UI. Anything not listed falls back to the ISO code, which
 * matches the prior `formatToParts`-based fallback (length > 2 → use code).
 *
 * Lives here rather than in `@commutifi/constants` so consumers don't need a
 * cross-repo bump. Move upstream when convenient.
 */
export const CURRENCY_SYMBOLS: Partial<Record<CurrencyCode, string>> = {
  // Dollar-ambiguous currencies (kept in sync with AMBIGUOUS_DOLLAR_CURRENCIES
  // below). All render with `$` for visual recognition; the ISO code beside
  // them in the picker resolves the ambiguity.
  [CurrencyCode.USD]: '$',
  [CurrencyCode.CAD]: '$',
  [CurrencyCode.AUD]: '$',
  [CurrencyCode.NZD]: '$',
  [CurrencyCode.SGD]: '$',
  [CurrencyCode.HKD]: '$',
  [CurrencyCode.MXN]: '$',
  [CurrencyCode.ARS]: '$',
  [CurrencyCode.CLP]: '$',
  [CurrencyCode.COP]: '$',
  [CurrencyCode.UYU]: '$',
  [CurrencyCode.EUR]: '€',
  [CurrencyCode.GBP]: '£',
  [CurrencyCode.JPY]: '¥',
  [CurrencyCode.CNY]: '¥',
  [CurrencyCode.KRW]: '₩',
  [CurrencyCode.INR]: '₹',
  [CurrencyCode.THB]: '฿',
  [CurrencyCode.TRY]: '₺',
  [CurrencyCode.ILS]: '₪',
  [CurrencyCode.VND]: '₫',
  [CurrencyCode.PHP]: '₱',
  [CurrencyCode.NGN]: '₦',
  [CurrencyCode.BDT]: '৳',
  [CurrencyCode.KZT]: '₸',
  [CurrencyCode.LAK]: '₭',
  [CurrencyCode.MNT]: '₮',
  [CurrencyCode.KHR]: '៛',
  [CurrencyCode.UAH]: '₴',
  [CurrencyCode.PYG]: '₲',
  [CurrencyCode.CRC]: '₡'
};

/**
 * Currencies that render as a bare `$` in some locales and are therefore
 * ambiguous with USD. Used by `pickDollarCurrencyDisplay` to decide whether
 * to force the ISO code instead of the symbol.
 *
 * Typed as `ReadonlySet<string>` so callers receiving a generic currency
 * string (e.g. from `react-intl` `FormatNumberOptions`) can probe the set
 * without an unsafe cast.
 */
export const AMBIGUOUS_DOLLAR_CURRENCIES: ReadonlySet<string> = new Set<string>([CurrencyCode.USD, CurrencyCode.CAD, CurrencyCode.AUD, CurrencyCode.NZD, CurrencyCode.SGD, CurrencyCode.HKD, CurrencyCode.MXN, CurrencyCode.ARS, CurrencyCode.CLP, CurrencyCode.COP, CurrencyCode.UYU]);